import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  BadgeEuro,
  Check,
  ChevronLeft,
  Coffee,
} from 'lucide-react';
import GuidedTutorial from './GuidedTutorial';
import {
  createOnboardingDraft,
  getPreviousOnboardingStep,
} from './onboardingState';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const visibleSteps = ['passwords', 'accounts', 'meals', 'employee', 'menu', 'payments'];

const formatError = error => error.response?.data?.detail || error.message || 'Die Änderung konnte nicht gespeichert werden.';

const ChoiceCard = ({ selected, onClick, title, children, disabled = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`relative rounded-xl border p-5 text-left transition-colors ${selected ? 'border-slate-700 bg-slate-50' : 'border-gray-200 bg-white hover:border-gray-300'} disabled:cursor-not-allowed disabled:opacity-60`}
  >
    {selected && <span className="absolute right-4 top-4 flex h-6 w-6 items-center justify-center rounded-full bg-slate-800 text-white"><Check className="h-4 w-4" /></span>}
    <div className="pr-8 font-semibold text-gray-950">{title}</div>
    <div className="mt-2 text-sm leading-6 text-gray-600">{children}</div>
  </button>
);

const ToggleRow = ({ checked, onChange, title, description }) => (
  <label className="flex cursor-pointer items-start justify-between gap-4 rounded-xl border border-gray-200 bg-white p-4">
    <span>
      <span className="block text-sm font-semibold text-gray-900">{title}</span>
      <span className="mt-1 block text-xs leading-5 text-gray-500">{description}</span>
    </span>
    <input type="checkbox" checked={checked} onChange={event => onChange(event.target.checked)} className="mt-1 h-4 w-4 rounded border-gray-300" />
  </label>
);

const DepartmentOnboarding = ({ currentDepartment, initialState, onCompleted, onPause, tutorialOnly = false }) => {
  const departmentId = currentDepartment?.department_id;
  const [draft, setDraft] = useState(() => {
    const initialDraft = createOnboardingDraft(initialState);
    return tutorialOnly ? { ...initialDraft, currentStep: 'tutorial' } : initialDraft;
  });
  const [readiness, setReadiness] = useState(initialState?.readiness || {});
  const [passwords, setPasswords] = useState({
    employee: '',
    employeeConfirmation: '',
    admin: '',
    adminConfirmation: '',
  });
  const [employeeName, setEmployeeName] = useState('');
  const [topping, setTopping] = useState({ name: 'Käse', price: '0.00' });
  const [product, setProduct] = useState({ type: 'drinks', name: 'Wasser', price: '1.50' });
  const [paypal, setPaypal] = useState({ enabled: false, link: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const step = draft.currentStep;
  const isTutorial = tutorialOnly || step === 'tutorial';
  const progress = useMemo(() => {
    const index = visibleSteps.indexOf(step);
    if (step === 'welcome') return 0;
    if (step === 'setup_complete' || step === 'tutorial') return 100;
    return Math.round(((Math.max(index, 0) + 1) / visibleSteps.length) * 100);
  }, [step]);

  useEffect(() => {
    if (step !== 'payments' || !departmentId) return;
    let active = true;
    axios.get(`${API}/department-paypal-settings/${departmentId}`)
      .then(response => {
        if (!active) return;
        const settings = response.data || {};
        setPaypal({
          enabled: Boolean(settings.enabled),
          link: settings.combined_link || settings.breakfast_link || settings.drinks_link || '',
        });
      })
      .catch(() => {});
    return () => { active = false; };
  }, [departmentId, step]);

  const runSave = async operation => {
    setSaving(true);
    setError('');
    try {
      await operation();
    } catch (saveError) {
      setError(formatError(saveError));
    } finally {
      setSaving(false);
    }
  };

  const checkpoint = async nextStep => {
    await axios.put(`${API}/department-admin/onboarding/${departmentId}/progress`, { step: nextStep });
    setDraft(current => ({ ...current, currentStep: nextStep }));
  };

  const navigateTo = async nextStep => {
    if (tutorialOnly) {
      setDraft(current => ({ ...current, currentStep: nextStep }));
      return;
    }
    await checkpoint(nextStep);
  };

  const goBack = () => runSave(async () => {
    const previous = getPreviousOnboardingStep(step);
    await navigateTo(previous);
  });

  const savePasswords = () => runSave(async () => {
    if (passwords.employee.length < 4 || passwords.admin.length < 4) {
      throw new Error('Beide Passwörter müssen mindestens 4 Zeichen lang sein.');
    }
    if (passwords.employee !== passwords.employeeConfirmation) {
      throw new Error('Die Mitarbeiter-Passwörter stimmen nicht überein.');
    }
    if (passwords.admin !== passwords.adminConfirmation) {
      throw new Error('Die Admin-Passwörter stimmen nicht überein.');
    }
    await axios.put(`${API}/department-admin/change-password/${departmentId}`, {
      new_employee_password: passwords.employee,
      new_admin_password: passwords.admin,
    });
    await checkpoint('accounts');
  });

  const saveAccounts = () => runSave(async () => {
    await axios.put(`${API}/department-admin/onboarding/${departmentId}/configuration`, {
      use_combined_accounts: draft.useCombinedAccounts,
    });
    await checkpoint('meals');
  });

  const saveMeals = () => runSave(async () => {
    await axios.put(`${API}/department-admin/onboarding/${departmentId}/configuration`, {
      is_breakfast_lunch_enabled: draft.breakfastLunchEnabled,
      is_drinks_enabled: draft.drinksEnabled,
      is_snacks_enabled: draft.snacksEnabled,
      dinner_enabled: draft.dinnerEnabled,
      default_lunch_price: Number.parseFloat(draft.defaultLunchPrice) || 0,
      default_dinner_price: Number.parseFloat(draft.defaultDinnerPrice) || 0,
    });
    await checkpoint('employee');
  });

  const saveEmployee = () => runSave(async () => {
    if (!readiness.has_employee) {
      if (!employeeName.trim()) throw new Error('Bitte gib einen Namen für den ersten Mitarbeiter ein.');
      const response = await axios.post(
        `${API}/department-admin/onboarding/${departmentId}/starter-data`,
        { employee_name: employeeName.trim() }
      );
      if (response.data.readiness) {
        setReadiness(response.data.readiness);
      } else {
        setReadiness(current => ({ ...current, has_employee: true }));
      }
    }
    await checkpoint('menu');
  });

  const saveStarterMenu = () => runSave(async () => {
    const starterData = {};
    if (draft.breakfastLunchEnabled && !readiness.has_topping) {
      if (!topping.name.trim() || !(Number.parseFloat(topping.price) >= 0)) throw new Error('Bitte vervollständige den ersten Belag.');
      starterData.topping_name = topping.name.trim();
      starterData.topping_price = Number.parseFloat(topping.price);
    }
    if (!readiness.has_product) {
      if (!product.name.trim() || !(Number.parseFloat(product.price) >= 0)) throw new Error('Bitte vervollständige das erste Produkt.');
      starterData.product_type = product.type;
      starterData.product_name = product.name.trim();
      starterData.product_price = Number.parseFloat(product.price);
    }
    if (Object.keys(starterData).length > 0) {
      const response = await axios.post(
        `${API}/department-admin/onboarding/${departmentId}/starter-data`,
        starterData
      );
      setReadiness(response.data.readiness || readiness);
    }
    await checkpoint('payments');
  });

  const savePayments = () => runSave(async () => {
    if (paypal.enabled && !paypal.link.trim()) {
      throw new Error('Bitte hinterlege einen PayPal-Link oder deaktiviere PayPal für den Moment.');
    }
    if (paypal.enabled && !draft.breakfastLunchEnabled && !draft.drinksEnabled && !draft.snacksEnabled) {
      throw new Error('PayPal benötigt mindestens einen aktivierten Bestellbereich.');
    }
    await axios.put(`${API}/department-admin/onboarding/${departmentId}/configuration`, {
      paypal_enabled: paypal.enabled,
      paypal_link: paypal.link.trim(),
    });
    await axios.post(`${API}/department-admin/onboarding/${departmentId}/complete`);
    setDraft(current => ({ ...current, currentStep: 'setup_complete' }));
  });

  const renderFooter = (onNext, nextLabel = 'Weiter') => (
    <div className="mt-7 flex flex-col-reverse gap-3 border-t border-gray-100 pt-5 sm:flex-row sm:items-center sm:justify-between">
      <button type="button" onClick={goBack} disabled={saving} className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
        <ChevronLeft className="h-4 w-4" /> Zurück
      </button>
      <button type="button" onClick={onNext} disabled={saving} className="rounded-lg bg-slate-800 px-5 py-2.5 text-sm font-medium text-white hover:bg-slate-700 disabled:cursor-wait disabled:opacity-60">
        {saving ? 'Wird gespeichert …' : nextLabel}
      </button>
    </div>
  );

  const renderStep = () => {
    if (step === 'welcome') {
      return (
        <div className="py-4 text-center sm:py-10">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-900 text-white"><Coffee className="h-7 w-7" /></div>
          <h1 className="mt-6 text-3xl font-semibold tracking-tight text-gray-950">Richten wir eure Wachabteilung ein</h1>
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-gray-600">
            In wenigen Schritten werden Konten, Mahlzeiten und erste Produkte eingerichtet. Anschließend kannst du die wichtigsten Funktionen in einer sicheren Vorschau kennenlernen.
          </p>
          <div className="mx-auto mt-7 grid max-w-2xl gap-3 text-left sm:grid-cols-3">
            {['Einstellungen wählen', 'Erste Daten anlegen', 'Funktionen kennenlernen'].map((label, index) => (
              <div key={label} className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <div className="text-xs font-semibold text-gray-400">0{index + 1}</div>
                <div className="mt-2 text-sm font-medium text-gray-800">{label}</div>
              </div>
            ))}
          </div>
          <div className="mt-8 flex items-center justify-center">
            <button type="button" onClick={() => runSave(() => checkpoint('passwords'))} disabled={saving} className="rounded-lg bg-slate-800 px-6 py-3 text-sm font-medium text-white hover:bg-slate-700">Einrichtung starten</button>
          </div>
        </div>
      );
    }

    if (step === 'passwords') {
      const passwordsMatch = passwords.employee === passwords.employeeConfirmation
        && passwords.admin === passwords.adminConfirmation;
      const samePassword = passwords.employee.length >= 4 && passwords.employee === passwords.admin;
      return (
        <div>
          <h2 className="text-2xl font-semibold text-gray-950">Zugänge absichern</h2>
          <p className="mt-2 text-sm leading-6 text-gray-600">Ersetze die beiden Initialpasswörter direkt zu Beginn. Das Mitarbeiter-Passwort öffnet die Bestellansicht, das Admin-Passwort schützt die Verwaltung.</p>
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
              <div className="text-sm font-semibold text-gray-900">Mitarbeiter-Passwort</div>
              <p className="mt-1 text-xs leading-5 text-gray-500">Für den normalen Login der Wachabteilung.</p>
              <div className="mt-4 space-y-3">
                <input name="employeePassword" type="password" autoComplete="new-password" value={passwords.employee} onChange={event => setPasswords(current => ({ ...current, employee: event.target.value }))} placeholder="Neues Passwort" className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5" />
                <input name="employeePasswordConfirmation" type="password" autoComplete="new-password" value={passwords.employeeConfirmation} onChange={event => setPasswords(current => ({ ...current, employeeConfirmation: event.target.value }))} placeholder="Passwort wiederholen" className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5" />
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
              <div className="text-sm font-semibold text-gray-900">Admin-Passwort</div>
              <p className="mt-1 text-xs leading-5 text-gray-500">Für Einstellungen, Preise und Mitarbeiterverwaltung.</p>
              <div className="mt-4 space-y-3">
                <input name="adminPassword" type="password" autoComplete="new-password" value={passwords.admin} onChange={event => setPasswords(current => ({ ...current, admin: event.target.value }))} placeholder="Neues Admin-Passwort" className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5" />
                <input name="adminPasswordConfirmation" type="password" autoComplete="new-password" value={passwords.adminConfirmation} onChange={event => setPasswords(current => ({ ...current, adminConfirmation: event.target.value }))} placeholder="Admin-Passwort wiederholen" className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5" />
              </div>
            </div>
          </div>
          <div className={`mt-4 rounded-lg border px-4 py-3 text-xs leading-5 ${samePassword ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-gray-200 bg-white text-gray-500'}`}>
            Mindestens 4 Zeichen. Unterschiedliche Passwörter für Mitarbeiter und Administration werden empfohlen.
          </div>
          {!passwordsMatch && (passwords.employeeConfirmation || passwords.adminConfirmation) && <p className="mt-3 text-sm text-red-700">Bitte prüfe die Passwort-Wiederholungen.</p>}
          {renderFooter(savePasswords, 'Passwörter speichern')}
        </div>
      );
    }

    if (step === 'accounts') {
      const accountLocked = Boolean(readiness.has_employee);
      return (
        <div>
          <h2 className="text-2xl font-semibold text-gray-950">Wie wird bei euch abgerechnet?</h2>
          <p className="mt-2 text-sm leading-6 text-gray-600">Diese Entscheidung bestimmt, ob Essen und Getränke getrennte Salden erhalten.</p>
          {accountLocked && <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600">Die Kontoart ist gesperrt, weil bereits ein Mitarbeiter angelegt wurde.</div>}
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <ChoiceCard selected={draft.useCombinedAccounts} disabled={accountLocked} onClick={() => setDraft(current => ({ ...current, useCombinedAccounts: true }))} title="Eine gemeinsame Kasse">
              Ein gemeinsamer Saldo für Frühstück, Essen, Getränke, Snacks und eigene Kategorien.
            </ChoiceCard>
            <ChoiceCard selected={!draft.useCombinedAccounts} disabled={accountLocked} onClick={() => setDraft(current => ({ ...current, useCombinedAccounts: false }))} title="Getrennte Konten">
              Ein Saldo für Frühstück und Essen sowie ein zweiter Saldo für Getränke und Snacks.
            </ChoiceCard>
          </div>
          {renderFooter(saveAccounts)}
        </div>
      );
    }

    if (step === 'meals') {
      return (
        <div>
          <h2 className="text-2xl font-semibold text-gray-950">Welche Bereiche möchtet ihr nutzen?</h2>
          <p className="mt-2 text-sm leading-6 text-gray-600">Alles lässt sich später unter Einstellungen erneut anpassen.</p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <ToggleRow checked={draft.breakfastLunchEnabled} onChange={value => setDraft(current => ({ ...current, breakfastLunchEnabled: value }))} title="Frühstück & Mittagessen" description="Brötchen, Beläge, Eier, Kaffee und Mittagessen." />
            <ToggleRow checked={draft.dinnerEnabled} onChange={value => setDraft(current => ({ ...current, dinnerEnabled: value }))} title="Abendessen" description="Für Nacht- oder individuelle Schichtprofile." />
            <ToggleRow checked={draft.drinksEnabled} onChange={value => setDraft(current => ({ ...current, drinksEnabled: value }))} title="Getränke" description="Produkte mit optionalem Lagerbestand." />
            <ToggleRow checked={draft.snacksEnabled} onChange={value => setDraft(current => ({ ...current, snacksEnabled: value }))} title="Snacks" description="Snacks und weitere Produkte getrennt anzeigen." />
          </div>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <label className="text-sm font-medium text-gray-700">Pauschaler Mittagspreis (€)<input type="number" min="0" step="0.01" value={draft.defaultLunchPrice} onChange={event => setDraft(current => ({ ...current, defaultLunchPrice: event.target.value }))} placeholder="Optional" className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2.5" /></label>
            <label className="text-sm font-medium text-gray-700">Pauschaler Abendpreis (€)<input type="number" min="0" step="0.01" value={draft.defaultDinnerPrice} onChange={event => setDraft(current => ({ ...current, defaultDinnerPrice: event.target.value }))} placeholder="Optional" disabled={!draft.dinnerEnabled} className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2.5 disabled:bg-gray-100" /></label>
          </div>
          {renderFooter(saveMeals)}
        </div>
      );
    }

    if (step === 'employee') {
      return (
        <div>
          <h2 className="text-2xl font-semibold text-gray-950">Der erste Mitarbeiter</h2>
          <p className="mt-2 text-sm leading-6 text-gray-600">Mitarbeiter bestellen über ihre Karte. Salden und Bestellverlauf werden automatisch ihrem Konto zugeordnet.</p>
          <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-5">
            {readiness.has_employee ? (
              <div className="flex items-center gap-3 text-sm font-medium text-gray-800"><span className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-800 text-white"><Check className="h-4 w-4" /></span>Mindestens ein Mitarbeiter ist bereits vorhanden.</div>
            ) : (
              <label className="block text-sm font-medium text-gray-700">Name<input autoFocus value={employeeName} onChange={event => setEmployeeName(event.target.value)} placeholder="z. B. Max Mustermann" className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5" /></label>
            )}
          </div>
          {renderFooter(saveEmployee, readiness.has_employee ? 'Mit vorhandenem Mitarbeiter weiter' : 'Mitarbeiter anlegen')}
        </div>
      );
    }

    if (step === 'menu') {
      const toppingReady = !draft.breakfastLunchEnabled || readiness.has_topping;
      return (
        <div>
          <h2 className="text-2xl font-semibold text-gray-950">Erste Auswahl anlegen</h2>
          <p className="mt-2 text-sm leading-6 text-gray-600">Damit die Bestellmaske nicht leer startet, legen wir die erste Auswahl für eure aktivierten Bereiche an.</p>
          <div className={`mt-6 grid gap-5 ${draft.breakfastLunchEnabled ? 'sm:grid-cols-2' : 'mx-auto max-w-xl'}`}>
            {draft.breakfastLunchEnabled && (
              <div className="rounded-xl border border-gray-200 p-5">
                <div className="font-semibold text-gray-900">Erster Frühstücksbelag</div>
                <p className="mt-1 text-xs leading-5 text-gray-500">Damit ist ein Belag für eine Brötchenhälfte gemeint, zum Beispiel Käse, Wurst oder Marmelade.</p>
                <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-xs leading-5 text-gray-600">
                  <span className="font-semibold text-gray-800">Preis festlegen:</span> Standardmäßig kannst du einen festen Brötchenpreis verwenden, zum Beispiel 0,80 € pro Brötchen, und alle Beläge mit 0,00 € anlegen. Alternativ setzt du einen Grundpreis für das Brötchen, zum Beispiel 0,30 €, und ergänzt je Belag einen eigenen Preis, zum Beispiel 0,50 €.
                </div>
                <p className="mt-3 text-xs leading-5 text-gray-500">Später kannst du einem Belag zusätzlich eine Einkaufsmenge zuweisen, zum Beispiel 50 g Mett pro Brötchen. Die benötigte Gesamtmenge wird dann automatisch in der Einkaufsliste zusammengerechnet.</p>
                {readiness.has_topping ? <p className="mt-3 text-sm text-gray-600">Ein Frühstücksbelag ist bereits vorhanden.</p> : <div className="mt-4 space-y-3"><label className="block text-xs font-medium text-gray-600">Name des Belags<input value={topping.name} onChange={event => setTopping(current => ({ ...current, name: event.target.value }))} placeholder="z. B. Käse" className="mt-1.5 w-full rounded-lg border px-3 py-2" /></label><label className="block text-xs font-medium text-gray-600">Zusätzlicher Preis pro Brötchenhälfte (€)<input type="number" min="0" step="0.01" value={topping.price} onChange={event => setTopping(current => ({ ...current, price: event.target.value }))} aria-label="Zusätzlicher Preis pro Brötchenhälfte in Euro" className="mt-1.5 w-full rounded-lg border px-3 py-2" /></label></div>}
              </div>
            )}
            <div className="rounded-xl border border-gray-200 p-5">
              <div className="font-semibold text-gray-900">Erstes Getränk oder erster Snack</div>
              {readiness.has_product ? <p className="mt-3 text-sm text-gray-600">Ein Getränk, Snack oder eigenes Produkt ist bereits vorhanden.</p> : <div className="mt-4 space-y-3"><label className="block text-xs font-medium text-gray-600">Bereich<select value={product.type} onChange={event => setProduct(current => ({ ...current, type: event.target.value }))} className="mt-1.5 w-full rounded-lg border px-3 py-2"><option value="drinks">Getränk</option><option value="sweets">Snack</option></select></label><label className="block text-xs font-medium text-gray-600">Produktname<input value={product.name} onChange={event => setProduct(current => ({ ...current, name: event.target.value }))} placeholder="z. B. Wasser" className="mt-1.5 w-full rounded-lg border px-3 py-2" /></label><label className="block text-xs font-medium text-gray-600">Preis pro Stück (€)<input type="number" min="0" step="0.01" value={product.price} onChange={event => setProduct(current => ({ ...current, price: event.target.value }))} aria-label="Preis pro Stück in Euro" className="mt-1.5 w-full rounded-lg border px-3 py-2" /></label></div>}
            </div>
          </div>
          {renderFooter(saveStarterMenu, toppingReady && readiness.has_product ? 'Mit vorhandener Auswahl weiter' : 'Auswahl anlegen')}
        </div>
      );
    }

    if (step === 'tutorial') {
      return <GuidedTutorial onDone={onCompleted} onSkip={onCompleted} />;
    }

    if (step === 'setup_complete') {
      return (
        <div className="py-5 text-center sm:py-9">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-slate-900 text-white"><Check className="h-7 w-7" /></div>
          <h2 className="mt-6 text-3xl font-semibold tracking-tight text-gray-950">Eure Wachabteilung ist eingerichtet</h2>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-gray-600">Passwörter, Konten, Module und erste Daten sind gespeichert. Als Nächstes kannst du die wichtigsten Funktionen in einem kurzen Tutorial kennenlernen.</p>
          <div className="mx-auto mt-7 max-w-xl rounded-xl border border-gray-200 bg-gray-50 p-4 text-left text-sm text-gray-600">
            Das Setup ist abgeschlossen. Das Tutorial verwendet nur Beispielansichten und erzeugt keine Bestellungen oder Buchungen.
          </div>
          <div className="mt-8 flex flex-col-reverse items-center justify-center gap-3 sm:flex-row">
            <button type="button" onClick={onCompleted} className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">Ohne Tutorial zum Dashboard</button>
            <button type="button" onClick={() => setDraft(current => ({ ...current, currentStep: 'tutorial' }))} className="rounded-lg bg-slate-800 px-6 py-2.5 text-sm font-medium text-white hover:bg-slate-700 active:scale-[0.98]">Tutorial starten</button>
          </div>
        </div>
      );
    }

    if (step === 'payments') {
      return (
        <div>
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gray-100 text-slate-700"><BadgeEuro className="h-5 w-5" /></div>
          <h2 className="mt-5 text-2xl font-semibold text-gray-950">Optional einen PayPal-Link hinterlegen</h2>
          <p className="mt-2 text-sm leading-6 text-gray-600">Bei einem negativen Saldo kann im Mitarbeiterverlauf ein externer Link zum PayPal-Konto eines Mitarbeiters angezeigt werden.</p>
          <div className="mt-4 rounded-lg border border-gray-200 bg-white px-4 py-3 text-xs leading-5 text-gray-600">
            Dies ist keine Zahlungsabwicklung der Kantinen-App. Der Link öffnet lediglich PayPal. Dort sendet der Nutzer den Betrag selbst, beispielsweise über „Freunde und Familie“. Zahlungseingänge werden nicht automatisch erkannt oder als Saldo verbucht.
          </div>
          <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-5">
            <ToggleRow checked={paypal.enabled} onChange={enabled => setPaypal(current => ({ ...current, enabled }))} title="Externen PayPal-Link anzeigen" description="Hinterlege den PayPal-Link der Person, die Zahlungen für die Wachabteilung entgegennimmt." />
            {paypal.enabled && (
              <label className="mt-4 block text-sm font-medium text-gray-700">
                Link zum PayPal-Konto eines Mitarbeiters
                <input value={paypal.link} onChange={event => setPaypal(current => ({ ...current, link: event.target.value }))} placeholder="paypal.me/benutzername" className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5" />
                <span className="mt-2 block text-xs font-normal leading-5 text-gray-500">Getrennte Links für Essen und Getränke können später unter Einstellungen konfiguriert werden.</span>
              </label>
            )}
          </div>
          {renderFooter(savePayments, paypal.enabled ? 'PayPal speichern' : 'Ohne PayPal weiter')}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="fixed inset-0 z-[80] overflow-y-auto bg-gray-100 px-3 py-4 sm:px-6 sm:py-8" role="dialog" aria-modal="true" aria-label="Wachabteilung einrichten">
      <div className="mx-auto w-full max-w-4xl overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl">
        <header className="border-b border-gray-200 bg-none bg-white px-5 py-4 sm:px-8">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-400">{isTutorial ? 'Tutorial' : 'Ersteinrichtung'}</div>
              <div className="mt-1 text-sm font-medium text-gray-800">{currentDepartment?.department_name}</div>
            </div>
            {visibleSteps.includes(step) && onPause && <button type="button" onClick={onPause} className="text-sm font-medium text-gray-500 hover:text-gray-800">Später fortsetzen</button>}
          </div>
          {step !== 'welcome' && !isTutorial && <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-gray-100"><div className="h-full rounded-full bg-slate-800 transition-[width] duration-300" style={{ width: `${progress}%` }} /></div>}
        </header>
        <main className="px-5 py-6 sm:px-8 sm:py-8">
          {error && <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>}
          {renderStep()}
        </main>
      </div>
    </div>
  );
};

export default DepartmentOnboarding;
